import React, { useState, useEffect } from 'react';
import { X, Search, Package, AlertCircle } from 'lucide-react';
import { productService, Product } from '../services/productService';
import { formatCurrency } from '../utils/format';
import toast from 'react-hot-toast';

interface ProductSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (product: Product) => void;
    title?: string;
    // New props for ad-hoc pre-order creation
    allowCreatePreOrder?: boolean;
    onCreatePreOrder?: (searchTerm: string, inventoryType: 'maestro' | 'novias') => void;
}

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    title = 'Seleccionar Producto',
    allowCreatePreOrder = false,
    onCreatePreOrder
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [inventoryType, setInventoryType] = useState<'maestro' | 'novias'>('maestro');

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setProducts([]);
            // Reset to default or keep previous selection? Resetting ensures clean state.
            setInventoryType('maestro');
        }
    }, [isOpen]);

    const handleSearch = async (term: string, currentType: 'maestro' | 'novias') => {
        setSearchTerm(term);
        if (term.length < 2) {
            setProducts([]);
            return;
        }

        setLoading(true);
        try {
            // 1. Search in current type
            let response = await productService.getProducts({
                search: term,
                limit: 20,
                type: currentType
            });

            // 2. If no results, try the other type
            if (response.data.length === 0) {
                const otherType = currentType === 'maestro' ? 'novias' : 'maestro';
                const otherResponse = await productService.getProducts({
                    search: term,
                    limit: 20,
                    type: otherType
                });

                if (otherResponse.data.length > 0) {
                    // Switch context
                    setInventoryType(otherType);
                    setProducts(otherResponse.data);
                    toast('Cambiado a Inventario ' + (otherType === 'maestro' ? 'Maestro' : 'Novias'), {
                        duration: 3000
                    });
                    return; // Exit early as we handled the state update
                }
            }

            // 3. Update with original results (empty or found)
            const filteredProducts = response.data.filter((p: any) => !p.pre_order);
            setProducts(filteredProducts);
        } catch (error) {
            console.error('Error searching products:', error);
            toast.error('Error al buscar productos');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                        <p className="text-sm text-gray-500 mt-1">Busca y selecciona el artículo correcto</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-200 rounded-full">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-100 bg-white">
                    {/* Inventory Type Selector */}
                    <div className="flex gap-6 mb-3 px-1">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <div className="relative flex items-center">
                                <input
                                    type="radio"
                                    name="inventoryType"
                                    value="maestro"
                                    checked={inventoryType === 'maestro'}
                                    onChange={() => {
                                        setInventoryType('maestro');
                                        if (searchTerm.length >= 2) {
                                            handleSearch(searchTerm, 'maestro');
                                        }
                                    }}
                                    className="peer h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                            </div>
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Inventario Maestro</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <div className="relative flex items-center">
                                <input
                                    type="radio"
                                    name="inventoryType"
                                    value="novias"
                                    checked={inventoryType === 'novias'}
                                    onChange={() => {
                                        setInventoryType('novias');
                                        if (searchTerm.length >= 2) {
                                            handleSearch(searchTerm, 'novias');
                                        }
                                    }}
                                    className="peer h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300"
                                />
                            </div>
                            <span className="text-sm font-medium text-gray-700 group-hover:text-pink-600 transition-colors">Inventario Novias</span>
                        </label>
                    </div>

                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por código, marca, color o talla..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value, inventoryType)}
                            className={`w-full pl-12 pr-4 py-3.5 border rounded-xl focus:ring-2 outline-none transition-all text-gray-800 bg-gray-50 focus:bg-white text-lg ${inventoryType === 'novias'
                                ? 'border-pink-200 focus:ring-pink-500/50 focus:border-pink-500'
                                : 'border-blue-200 focus:ring-blue-500/50 focus:border-blue-500'
                                }`}
                            autoFocus
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 bg-gray-50/30">
                    {loading ? (
                        <div className="text-center py-12 text-gray-500">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                            <p className="font-medium animate-pulse">Buscando productos...</p>
                        </div>
                    ) : products.length > 0 ? (
                        <div className="space-y-3">
                            {products.map((product) => (
                                <div
                                    key={product.id_producto}
                                    onClick={() => {
                                        if ((product.stock || 0) > 0) {
                                            onSelect({ ...product, inventoryType });
                                        }
                                    }}
                                    className={`group p-4 border rounded-xl flex justify-between items-center transition-all duration-200 relative overflow-hidden ${(product.stock || 0) <= 0
                                        ? 'opacity-60 bg-gray-100 cursor-not-allowed grayscale'
                                        : 'bg-white border-gray-200 cursor-pointer hover:border-blue-400 hover:shadow-md hover:translate-y-[-1px]'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${(product.stock || 0) > 0 ? 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
                                            <Package size={28} strokeWidth={1.5} />
                                        </div>
                                        <div>
                                            <div className="flex flex-col gap-0.5 mb-1.5">
                                                <h3 className="font-bold text-gray-900 text-lg tracking-tight group-hover:text-blue-700 transition-colors font-mono">
                                                    {product.cod_producto}
                                                </h3>
                                                <span className="text-sm text-gray-500 font-medium">
                                                    {product.marca}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3 text-sm">
                                                {product.talla && (
                                                    <span className={`px-2 py-0.5 rounded text-xs font-bold border ${(product.stock || 0) > 0
                                                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                        : 'bg-gray-200 text-gray-600 border-gray-300'
                                                        }`}>
                                                        Talla: {product.talla}
                                                    </span>
                                                )}
                                                {product.color && (
                                                    <span className="text-gray-600 flex items-center gap-1.5 font-medium">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-gray-300 ring-1 ring-gray-100"></span>
                                                        {product.color}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="mt-1.5 flex gap-2">
                                                {(product.stock || 0) <= 0 && (
                                                    <span className="text-[10px] uppercase font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">🚫 Sin Stock</span>
                                                )}
                                                {product.pre_order && (
                                                    <span className="text-[10px] uppercase font-bold text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded border border-yellow-100 flex items-center gap-1">
                                                        <AlertCircle size={10} className="text-yellow-600" />
                                                        Pre-order
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right pl-4 border-l border-gray-100 ml-2">
                                        <p className="font-bold text-gray-900 text-xl tracking-tight">{formatCurrency(product.precio || 0)}</p>
                                        <p className={`text-sm font-semibold mt-0.5 ${(product.stock || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {(product.stock || 0) > 0 ? `${product.stock} Disp.` : 'Agotado'}
                                        </p>
                                    </div>

                                    {/* Hover Indicator */}
                                    {(product.stock || 0) > 0 && (
                                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : searchTerm.length >= 2 ? (
                        <div className="text-center py-12 text-gray-500">
                            <Package size={48} className="mx-auto text-gray-300 mb-2" />
                            <p>No se encontraron productos</p>
                            {allowCreatePreOrder && onCreatePreOrder && (
                                <button
                                    onClick={() => onCreatePreOrder(searchTerm, inventoryType)}
                                    className="mt-4 px-6 py-2 bg-yellow-600 text-white rounded-lg font-bold hover:bg-yellow-700 transition shadow-md flex items-center gap-2 mx-auto"
                                >
                                    <Package size={18} />
                                    Crear Pre-order: "{searchTerm}"
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-400">
                            <Search size={48} className="mx-auto text-gray-200 mb-2" />
                            <p>Ingrese al menos 2 caracteres para buscar</p>
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-xl text-center">
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-sm font-medium">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};
